import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import type { Serie, Rubrique } from '@/types';

interface CachedItem {
  data: any;
  timestamp: number;
  ttl: number;
}

interface Top3CacheItem {
  data: Serie[];
  cachedDate: string;
}

interface ContentCacheStore {
  series: Record<string, Serie[]>;
  rubriques: Record<string, Rubrique[]>;
  seriesByRubrique: Record<string, Record<string, Serie[]>>;
  topViewedSeries: Record<string, Serie[]>;
  homepageTop3: Record<string, Top3CacheItem>;
  freeEpisodesSeries: Record<string, Serie[]>;
  genericCache: Record<string, CachedItem>;
  timestamp: number;
  campaignId: string;
}

function getTodayDateString(): string {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
}

function isTop3CacheValidForToday(cachedDate: string): boolean {
  return cachedDate === getTodayDateString();
}

const CACHE_KEY = '@content_cache';
const CACHE_RETENTION_HOURS = 12;
const PERSIST_DEBOUNCE_MS = 500;

const CACHE_TTL_CONFIG = {
  series: 24 * 60 * 60 * 1000, // 24 hours
  rubriques: 24 * 60 * 60 * 1000, // 24 hours
  episodes: 12 * 60 * 60 * 1000, // 12 hours
  seriesByRubrique: 6 * 60 * 60 * 1000, // 6 hours
  featured: 1 * 60 * 60 * 1000, // 1 hour for featured content
};

class PersistentContentCacheService {
  private seriesCache: Map<string, Serie[]> = new Map();
  private rubriquesCache: Map<string, Rubrique[]> = new Map();
  private seriesByRubriqueCache: Map<string, Map<string, Serie[]>> = new Map();
  private topViewedSeriesCache: Map<string, Serie[]> = new Map();
  private homepageTop3Cache: Map<string, Top3CacheItem> = new Map();
  private freeEpisodesSeriesCache: Map<string, Serie[]> = new Map();
  private genericCache: Map<string, CachedItem> = new Map();
  private initialized = false;
  private initializationPromise: Promise<void> | null = null;
  private currentCampaignId: string | null = null;
  private quotaExhausted = false;
  private persistDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private persistPromiseResolvers: Array<{ resolve: () => void; reject: (err: unknown) => void }> = [];

  async initialize(campaignId?: string): Promise<void> {
    if (this.initialized && (!campaignId || campaignId === this.currentCampaignId)) {
      return;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this._initializeInternal(campaignId);
    return this.initializationPromise;
  }

  private async _initializeInternal(campaignId?: string): Promise<void> {
    const startTime = Date.now();
    console.log('📦 [CONTENT-CACHE] Initializing persistent content cache...');

    try {
      const storedCache = await AsyncStorage.getItem(CACHE_KEY);

      if (storedCache) {
        const parsed: ContentCacheStore = JSON.parse(storedCache);
        const now = Date.now();
        const age = now - parsed.timestamp;
        const maxAge = CACHE_RETENTION_HOURS * 60 * 60 * 1000;

        if (campaignId && parsed.campaignId !== campaignId) {
          console.log(`📦 [CONTENT-CACHE] Campaign changed (${parsed.campaignId} → ${campaignId}), clearing cache`);
          await this.clear();
        } else if (age > maxAge) {
          console.log(`📦 [CONTENT-CACHE] Cache expired (age: ${Math.round(age / 1000 / 60)}min), clearing`);
          await this.clear();
        } else {
          this.seriesCache = new Map(Object.entries(parsed.series));
          this.rubriquesCache = new Map(Object.entries(parsed.rubriques));
          this.topViewedSeriesCache = new Map(Object.entries(parsed.topViewedSeries));
          this.homepageTop3Cache = new Map(Object.entries(parsed.homepageTop3 || {}));
          this.freeEpisodesSeriesCache = new Map(Object.entries(parsed.freeEpisodesSeries));
          this.genericCache = new Map(Object.entries(parsed.genericCache || {}));

          this.seriesByRubriqueCache = new Map();
          Object.entries(parsed.seriesByRubrique).forEach(([campaignKey, rubriqueMap]) => {
            this.seriesByRubriqueCache.set(campaignKey, new Map(Object.entries(rubriqueMap)));
          });

          this.currentCampaignId = parsed.campaignId;
          const duration = Date.now() - startTime;
          console.log(`📦 [CONTENT-CACHE] ✅ Loaded cache in ${duration}ms`);
          console.log(`📦 [CONTENT-CACHE]   - Series: ${this.seriesCache.size} entries`);
          console.log(`📦 [CONTENT-CACHE]   - Rubriques: ${this.rubriquesCache.size} entries`);
          console.log(`📦 [CONTENT-CACHE]   - Series by Rubrique: ${this.seriesByRubriqueCache.size} entries`);
        }
      } else {
        console.log('📦 [CONTENT-CACHE] No cached data found, starting fresh');
      }

      if (campaignId) {
        this.currentCampaignId = campaignId;
      }

      this.initialized = true;
    } catch (error) {
      console.error('📦 [CONTENT-CACHE] Error initializing cache:', error);
      this.clearMemory();
      this.initialized = true;
    } finally {
      this.initializationPromise = null;
    }
  }

  private clearMemory(): void {
    this.seriesCache.clear();
    this.rubriquesCache.clear();
    this.seriesByRubriqueCache.clear();
    this.topViewedSeriesCache.clear();
    this.homepageTop3Cache.clear();
    this.freeEpisodesSeriesCache.clear();
    this.genericCache.clear();
  }

  private isQuotaExceededError(error: unknown): boolean {
    if (error instanceof DOMException && error.name === 'QuotaExceededError') return true;
    if (error instanceof Error && error.message?.includes('QuotaExceededError')) return true;
    if (error instanceof Error && error.message?.includes('exceeded the quota')) return true;
    return false;
  }

  private buildStoreObject(): ContentCacheStore {
    const seriesByRubriqueObj: Record<string, Record<string, Serie[]>> = {};
    this.seriesByRubriqueCache.forEach((rubriqueMap, campaignKey) => {
      seriesByRubriqueObj[campaignKey] = Object.fromEntries(rubriqueMap);
    });

    return {
      series: Object.fromEntries(this.seriesCache),
      rubriques: Object.fromEntries(this.rubriquesCache),
      seriesByRubrique: seriesByRubriqueObj,
      topViewedSeries: Object.fromEntries(this.topViewedSeriesCache),
      homepageTop3: Object.fromEntries(this.homepageTop3Cache),
      freeEpisodesSeries: Object.fromEntries(this.freeEpisodesSeriesCache),
      genericCache: Object.fromEntries(this.genericCache),
      timestamp: Date.now(),
      campaignId: this.currentCampaignId!,
    };
  }

  private async persistCacheImmediate(): Promise<void> {
    if (!this.currentCampaignId) {
      console.warn('📦 [CONTENT-CACHE] Cannot persist cache without campaign ID');
      return;
    }

    try {
      const store = this.buildStoreObject();
      await AsyncStorage.setItem(CACHE_KEY, JSON.stringify(store));
      this.quotaExhausted = false;
      console.log(`📦 [CONTENT-CACHE] ✅ Persisted cache for campaign: ${this.currentCampaignId}`);
    } catch (error) {
      if (this.isQuotaExceededError(error) && Platform.OS === 'web') {
        console.warn('📦 [CONTENT-CACHE] QuotaExceededError detected, attempting progressive eviction...');
        await this.persistWithProgressiveEviction();
      } else {
        console.error('📦 [CONTENT-CACHE] Error persisting cache:', error);
      }
    }
  }

  private async persistWithProgressiveEviction(): Promise<void> {
    const evictionLevels: Array<{ name: string; evict: () => void }> = [
      {
        name: 'genericCache',
        evict: () => this.genericCache.clear(),
      },
      {
        name: 'topViewedSeries + freeEpisodesSeries',
        evict: () => {
          this.topViewedSeriesCache.clear();
          this.freeEpisodesSeriesCache.clear();
        },
      },
      {
        name: 'homepageTop3',
        evict: () => this.homepageTop3Cache.clear(),
      },
      {
        name: 'seriesByRubrique',
        evict: () => this.seriesByRubriqueCache.clear(),
      },
    ];

    for (let level = 0; level < evictionLevels.length; level++) {
      const { name, evict } = evictionLevels[level];
      evict();
      console.warn(`📦 [CONTENT-CACHE] Eviction level ${level + 1}: cleared ${name}`);

      try {
        const store = this.buildStoreObject();
        await AsyncStorage.setItem(CACHE_KEY, JSON.stringify(store));
        this.quotaExhausted = false;
        console.log(`📦 [CONTENT-CACHE] ✅ Persisted cache after eviction level ${level + 1} (cleared: ${name})`);
        return;
      } catch (retryError) {
        if (!this.isQuotaExceededError(retryError)) {
          console.error('📦 [CONTENT-CACHE] Non-quota error during eviction retry:', retryError);
          return;
        }
      }
    }

    this.quotaExhausted = true;
    console.warn('📦 [CONTENT-CACHE] All eviction levels exhausted, cache will operate in memory-only mode for this session');
  }

  private persistCache(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.persistPromiseResolvers.push({ resolve, reject });

      if (this.persistDebounceTimer) {
        clearTimeout(this.persistDebounceTimer);
      }

      this.persistDebounceTimer = setTimeout(async () => {
        this.persistDebounceTimer = null;
        const resolvers = [...this.persistPromiseResolvers];
        this.persistPromiseResolvers = [];

        try {
          await this.persistCacheImmediate();
          resolvers.forEach(r => r.resolve());
        } catch (error) {
          resolvers.forEach(r => r.reject(error));
        }
      }, PERSIST_DEBOUNCE_MS);
    });
  }

  async getSeries(campaignId: string): Promise<Serie[] | null> {
    await this.initialize(campaignId);
    const series = this.seriesCache.get(campaignId);

    if (series) {
      console.log(`📦 [CONTENT-CACHE] ✅ HIT for series (campaign: ${campaignId})`);
      return series;
    }

    console.log(`📦 [CONTENT-CACHE] ❌ MISS for series (campaign: ${campaignId})`);
    return null;
  }

  async setSeries(campaignId: string, series: Serie[]): Promise<void> {
    await this.initialize(campaignId);

    if (!series || series.length === 0) {
      console.log(`📦 [CONTENT-CACHE] ⚠️ Refusing to cache empty series for campaign: ${campaignId}`);
      return;
    }

    this.seriesCache.set(campaignId, series);
    console.log(`📦 [CONTENT-CACHE] ✅ Cached ${series.length} series for campaign: ${campaignId}`);
    await this.persistCache();
  }

  async getRubriques(campaignId: string): Promise<Rubrique[] | null> {
    await this.initialize(campaignId);
    const rubriques = this.rubriquesCache.get(campaignId);

    if (rubriques) {
      console.log(`📦 [CONTENT-CACHE] ✅ HIT for rubriques (campaign: ${campaignId})`);
      return rubriques;
    }

    console.log(`📦 [CONTENT-CACHE] ❌ MISS for rubriques (campaign: ${campaignId})`);
    return null;
  }

  async setRubriques(campaignId: string, rubriques: Rubrique[]): Promise<void> {
    await this.initialize(campaignId);

    if (!rubriques || rubriques.length === 0) {
      console.log(`📦 [CONTENT-CACHE] ⚠️ Refusing to cache empty rubriques for campaign: ${campaignId}`);
      return;
    }

    this.rubriquesCache.set(campaignId, rubriques);
    console.log(`📦 [CONTENT-CACHE] ✅ Cached ${rubriques.length} rubriques for campaign: ${campaignId}`);
    await this.persistCache();
  }

  async getSeriesByRubrique(campaignId: string, rubriqueId: string): Promise<Serie[] | null> {
    await this.initialize(campaignId);
    const rubriqueMap = this.seriesByRubriqueCache.get(campaignId);

    if (rubriqueMap) {
      const series = rubriqueMap.get(rubriqueId);
      if (series) {
        console.log(`📦 [CONTENT-CACHE] ✅ HIT for series by rubrique (campaign: ${campaignId}, rubrique: ${rubriqueId})`);
        return series;
      }
    }

    console.log(`📦 [CONTENT-CACHE] ❌ MISS for series by rubrique (campaign: ${campaignId}, rubrique: ${rubriqueId})`);
    return null;
  }

  async setSeriesByRubrique(campaignId: string, rubriqueId: string, series: Serie[]): Promise<void> {
    await this.initialize(campaignId);

    if (!series || series.length === 0) {
      console.log(`📦 [CONTENT-CACHE] ⚠️ Refusing to cache empty series for rubrique ${rubriqueId} (campaign: ${campaignId})`);
      return;
    }

    let rubriqueMap = this.seriesByRubriqueCache.get(campaignId);
    if (!rubriqueMap) {
      rubriqueMap = new Map();
      this.seriesByRubriqueCache.set(campaignId, rubriqueMap);
    }

    rubriqueMap.set(rubriqueId, series);
    console.log(`📦 [CONTENT-CACHE] ✅ Cached ${series.length} series for rubrique ${rubriqueId} (campaign: ${campaignId})`);
    await this.persistCache();
  }

  async getTopViewedSeries(campaignId: string): Promise<Serie[] | null> {
    await this.initialize(campaignId);
    const series = this.topViewedSeriesCache.get(campaignId);

    if (series) {
      console.log(`📦 [CONTENT-CACHE] ✅ HIT for top viewed series (campaign: ${campaignId})`);
      return series;
    }

    console.log(`📦 [CONTENT-CACHE] ❌ MISS for top viewed series (campaign: ${campaignId})`);
    return null;
  }

  async setTopViewedSeries(campaignId: string, series: Serie[]): Promise<void> {
    await this.initialize(campaignId);

    if (!series || series.length === 0) {
      console.log(`📦 [CONTENT-CACHE] ⚠️ Refusing to cache empty top viewed series for campaign: ${campaignId}`);
      return;
    }

    this.topViewedSeriesCache.set(campaignId, series);
    console.log(`📦 [CONTENT-CACHE] ✅ Cached ${series.length} top viewed series for campaign: ${campaignId}`);
    await this.persistCache();
  }

  async getHomepageTop3(campaignId: string, contentMetadataCampaignId: string | null = null): Promise<Serie[] | null> {
    await this.initialize(campaignId);
    // Key by content-metadata campaign too, so each selected language caches
    // separately. Bumped to _v3 (invalidates the old primary-only entries).
    const key = `${campaignId}_${contentMetadataCampaignId ?? 'none'}_v3`;
    const cacheItem = this.homepageTop3Cache.get(key);

    if (cacheItem) {
      const todayStr = getTodayDateString();
      if (isTop3CacheValidForToday(cacheItem.cachedDate)) {
        console.log(`📦 [CONTENT-CACHE] ✅ HIT for homepage top 3 (campaign: ${campaignId}, date: ${cacheItem.cachedDate})`);
        return cacheItem.data;
      } else {
        console.log(`📦 [CONTENT-CACHE] ⏰ TOP3 cache expired (cached: ${cacheItem.cachedDate}, today: ${todayStr}), needs refresh`);
        this.homepageTop3Cache.delete(key);
        await this.persistCache();
        return null;
      }
    }

    console.log(`📦 [CONTENT-CACHE] ❌ MISS for homepage top 3 (campaign: ${campaignId})`);
    return null;
  }

  async setHomepageTop3(campaignId: string, series: Serie[], contentMetadataCampaignId: string | null = null): Promise<void> {
    await this.initialize(campaignId);

    if (!series || series.length === 0) {
      console.log(`📦 [CONTENT-CACHE] ⚠️ Refusing to cache empty homepage top 3 for campaign: ${campaignId}`);
      return;
    }

    const todayStr = getTodayDateString();
    const cacheItem: Top3CacheItem = {
      data: series,
      cachedDate: todayStr,
    };
    this.homepageTop3Cache.set(`${campaignId}_${contentMetadataCampaignId ?? 'none'}_v3`, cacheItem);
    console.log(`📦 [CONTENT-CACHE] ✅ Cached ${series.length} homepage top 3 items for campaign: ${campaignId} (date: ${todayStr})`);
    await this.persistCache();
  }

  async getFreeEpisodesSeries(campaignId: string): Promise<Serie[] | null> {
    await this.initialize(campaignId);
    const series = this.freeEpisodesSeriesCache.get(campaignId);

    if (series) {
      console.log(`📦 [CONTENT-CACHE] ✅ HIT for free episodes series (campaign: ${campaignId})`);
      return series;
    }

    console.log(`📦 [CONTENT-CACHE] ❌ MISS for free episodes series (campaign: ${campaignId})`);
    return null;
  }

  async setFreeEpisodesSeries(campaignId: string, series: Serie[]): Promise<void> {
    await this.initialize(campaignId);

    if (!series || series.length === 0) {
      console.log(`📦 [CONTENT-CACHE] ⚠️ Refusing to cache empty free episodes series for campaign: ${campaignId}`);
      return;
    }

    this.freeEpisodesSeriesCache.set(campaignId, series);
    console.log(`📦 [CONTENT-CACHE] ✅ Cached ${series.length} free episodes series for campaign: ${campaignId}`);
    await this.persistCache();
  }

  async clear(): Promise<void> {
    console.log('📦 [CONTENT-CACHE] Clearing cache');
    if (this.persistDebounceTimer) {
      clearTimeout(this.persistDebounceTimer);
      this.persistDebounceTimer = null;
      const resolvers = [...this.persistPromiseResolvers];
      this.persistPromiseResolvers = [];
      resolvers.forEach(r => r.resolve());
    }
    this.clearMemory();
    this.currentCampaignId = null;
    this.initialized = false;
    this.quotaExhausted = false;
    await AsyncStorage.removeItem(CACHE_KEY);
  }

  async clearCampaign(campaignId: string): Promise<void> {
    console.log(`📦 [CONTENT-CACHE] Clearing cache for campaign: ${campaignId}`);

    this.seriesCache.delete(campaignId);
    this.rubriquesCache.delete(campaignId);
    this.seriesByRubriqueCache.delete(campaignId);
    this.topViewedSeriesCache.delete(campaignId);
    this.homepageTop3Cache.delete(campaignId);
    this.freeEpisodesSeriesCache.delete(campaignId);

    const genericKeysToDelete: string[] = [];
    this.genericCache.forEach((_, key) => {
      if (key.startsWith(`${campaignId}_`)) {
        genericKeysToDelete.push(key);
      }
    });

    genericKeysToDelete.forEach(key => this.genericCache.delete(key));

    if (this.persistDebounceTimer) {
      clearTimeout(this.persistDebounceTimer);
      this.persistDebounceTimer = null;
      const resolvers = [...this.persistPromiseResolvers];
      this.persistPromiseResolvers = [];
      resolvers.forEach(r => r.resolve());
    }
    await this.persistCacheImmediate();
    console.log(`📦 [CONTENT-CACHE] ✅ Cleared campaign ${campaignId} cache`);
  }

  async getCacheStats(): Promise<{
    initialized: boolean;
    campaignId: string | null;
    seriesCount: number;
    rubriquesCount: number;
    seriesByRubriqueCount: number;
  }> {
    await this.initialize();

    return {
      initialized: this.initialized,
      campaignId: this.currentCampaignId,
      seriesCount: this.seriesCache.size,
      rubriquesCount: this.rubriquesCache.size,
      seriesByRubriqueCount: this.seriesByRubriqueCache.size,
    };
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  getAllCachedSeries(campaignId: string): Serie[] {
    const series = this.seriesCache.get(campaignId);
    return series || [];
  }

  getCurrentCampaignId(): string | null {
    return this.currentCampaignId;
  }

  async getAllSeries(campaignId?: string): Promise<Serie[] | null> {
    const targetCampaignId = campaignId || this.currentCampaignId;

    if (!targetCampaignId) {
      console.log('📦 [CONTENT-CACHE] ⚠️ No campaign ID for getAllSeries');
      return null;
    }

    await this.initialize(targetCampaignId);
    const series = this.seriesCache.get(targetCampaignId);

    if (series) {
      console.log(`📦 [CONTENT-CACHE] ✅ getAllSeries: Found ${series.length} series for campaign ${targetCampaignId}`);
      return series;
    }

    console.log(`📦 [CONTENT-CACHE] ❌ getAllSeries: No series found for campaign ${targetCampaignId}`);
    return null;
  }

  async get<T>(key: string, campaignId: string): Promise<T | null> {
    await this.initialize(campaignId);

    const cacheKey = `${campaignId}_${key}`;
    const cached = this.genericCache.get(cacheKey);

    if (!cached) {
      console.log(`📦 [CONTENT-CACHE] ❌ MISS for generic key: "${key}"`);
      return null;
    }

    const now = Date.now();
    const age = now - cached.timestamp;

    if (age > cached.ttl) {
      const ageMinutes = Math.round(age / 60000);
      console.log(`📦 [CONTENT-CACHE] ⏰ EXPIRED for generic key: "${key}" (age: ${ageMinutes}m)`);
      this.genericCache.delete(cacheKey);
      await this.persistCache();
      return null;
    }

    const ageMinutes = Math.round(age / 60000);
    console.log(`📦 [CONTENT-CACHE] ✅ HIT for generic key: "${key}" (age: ${ageMinutes}m)`);
    return cached.data as T;
  }

  async set<T>(
    key: string,
    campaignId: string,
    data: T,
    ttl: number = 24 * 60 * 60 * 1000
  ): Promise<void> {
    await this.initialize(campaignId);

    const cacheKey = `${campaignId}_${key}`;
    const cached: CachedItem = {
      data,
      timestamp: Date.now(),
      ttl,
    };

    this.genericCache.set(cacheKey, cached);
    await this.persistCache();

    const ttlHours = Math.round(ttl / 3600000);
    console.log(`📦 [CONTENT-CACHE] 💾 STORED generic key: "${key}" (TTL: ${ttlHours}h)`);
  }

  getTTL(contentType: keyof typeof CACHE_TTL_CONFIG): number {
    return CACHE_TTL_CONFIG[contentType] || 24 * 60 * 60 * 1000;
  }

  getSeriesCount(campaignId?: string): number {
    if (campaignId) {
      const series = this.seriesCache.get(campaignId);
      return series ? series.length : 0;
    }

    let total = 0;
    this.seriesCache.forEach((seriesArray) => {
      total += seriesArray.length;
    });
    return total;
  }

  getRubriquesCount(campaignId?: string): number {
    if (campaignId) {
      const rubriques = this.rubriquesCache.get(campaignId);
      return rubriques ? rubriques.length : 0;
    }

    let total = 0;
    this.rubriquesCache.forEach((rubriquesArray) => {
      total += rubriquesArray.length;
    });
    return total;
  }

  getSeriesIds(campaignId: string): string[] {
    const series = this.seriesCache.get(campaignId);
    if (!series) return [];
    return series.map(s => s.id);
  }

  async getDetailedCacheStats(): Promise<{
    initialized: boolean;
    campaignId: string | null;
    seriesCount: number;
    rubriquesCount: number;
    seriesByRubriqueCount: number;
    totalSeriesAcrossAllCampaigns: number;
    totalRubriquesAcrossAllCampaigns: number;
    genericCacheCount: number;
  }> {
    await this.initialize();

    return {
      initialized: this.initialized,
      campaignId: this.currentCampaignId,
      seriesCount: this.seriesCache.size,
      rubriquesCount: this.rubriquesCache.size,
      seriesByRubriqueCount: this.seriesByRubriqueCache.size,
      totalSeriesAcrossAllCampaigns: this.getSeriesCount(),
      totalRubriquesAcrossAllCampaigns: this.getRubriquesCount(),
      genericCacheCount: this.genericCache.size,
    };
  }

  async hasData(campaignId?: string): Promise<boolean> {
    await this.initialize(campaignId);

    const targetCampaignId = campaignId || this.currentCampaignId;

    if (!targetCampaignId) {
      console.log('📦 [CONTENT-CACHE] ❌ No campaign ID to check for data');
      return false;
    }

    const seriesCount = this.getSeriesCount(targetCampaignId);
    const rubriquesCount = this.getRubriquesCount(targetCampaignId);

    const hasData = seriesCount > 0 || rubriquesCount > 0;

    console.log(`📦 [CONTENT-CACHE] Cache data check for campaign ${targetCampaignId}:`);
    console.log(`📦 [CONTENT-CACHE]   - Series: ${seriesCount}`);
    console.log(`📦 [CONTENT-CACHE]   - Rubriques: ${rubriquesCount}`);
    console.log(`📦 [CONTENT-CACHE]   - Has data: ${hasData ? '✅ YES' : '❌ NO'}`);

    return hasData;
  }
}

export const persistentContentCache = new PersistentContentCacheService();
